import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  CircularProgress,
  Paper,
  Stepper,
  Step,
  StepLabel
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import useLocales from 'hooks/useLocales';
import Page from 'components/containers/Page';
import { useSnackbar } from 'notistack';
import { RootState } from 'redux/store';
import { fetchHrmQuestions, submitHrmAnswers } from 'redux/slices/assessment';
import { HrmAnswerSubmission } from 'requests/assessment';
import {
  ArrowBackIosNew,
  NavigateBefore,
  NavigateNext
} from '@material-ui/icons';
import { getButtons } from 'constants/text/buttons';
import HomeNavbar from 'layouts/home/HomeNavbar';
import DashboardNavbar from 'layouts/dashboard/navbar/DashboardNavbar';
import useAuth from '@hooks/useAuth';
import PageContainer from '@components/containers/PageContainer';

const useStyles = makeStyles((theme: any) => ({
  questionCard: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.customShadows.z8,
    transition: 'all 0.3s ease-in-out'
  },
  questionText: {
    fontWeight: 600,
    marginBottom: theme.spacing(2)
  },
  radioGroup: {
    marginTop: theme.spacing(1)
  },
  progressContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px'
  },
  submitButton: {
    background: 'linear-gradient(92.25deg, #9B5FDA -2.57%, #562C82 113.38%)',
    color: '#FFFFFF',
    borderRadius: '30px',
    width: 'fit-content',
    marginTop: theme.spacing(4),
    fontSize: '21px',
    fontWeight: 600,
    fontFamily: 'Rubik'
  },
  submitButtonDisabled: {
    background: 'grey',
    color: '#FFFFFF',
    borderRadius: '30px',
    width: 'fit-content',
    marginTop: theme.spacing(4),
    fontSize: '21px',
    fontWeight: 600,
    fontFamily: 'Rubik'
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(4),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius
  },
  paginationButton: {
    minWidth: '120px',
    borderRadius: '25px',
    textTransform: 'none',
    fontWeight: 600
  },
  progressStepper: {
    marginBottom: theme.spacing(4),
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    '& .MuiStepper-root': {
      overflow: 'auto',
      padding: theme.spacing(1, 0),
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.5, 0)
      }
    },
    '& .MuiStepLabel-root': {
      minWidth: '80px',
      [theme.breakpoints.down('sm')]: {
        minWidth: '60px'
      },
      '& .MuiStepLabel-label': {
        fontSize: '0.75rem',
        wordBreak: 'break-word',
        [theme.breakpoints.down('sm')]: {
          fontSize: '0.65rem'
        }
      }
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
      marginBottom: theme.spacing(2)
    }
  },
  keyboardHint: {
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1, 2),
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.875rem',
    zIndex: 1000
  },
  pageIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  currentPage: {
    fontWeight: 600,
    color: theme.palette.primary.main
  },
  privacyOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    backdropFilter: 'blur(10px)'
  },
  privacyContent: {
    textAlign: 'center',
    color: 'white',
    maxWidth: '500px',
    padding: theme.spacing(4)
  },
  privacyTitle: {
    fontSize: '2rem',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: 'white'
  },
  privacyDescription: {
    fontSize: '1.1rem',
    marginBottom: theme.spacing(4),
    lineHeight: 1.6,
    color: 'rgba(255, 255, 255, 0.9)'
  },
  startButton: {
    background: 'linear-gradient(92.25deg, #9B5FDA -2.57%, #562C82 113.38%)',
    color: '#FFFFFF',
    borderRadius: '30px',
    padding: theme.spacing(2, 4),
    fontSize: '1.2rem',
    fontWeight: 600,
    fontFamily: 'Rubik',
    textTransform: 'none',
    minWidth: '200px',
    '&:hover': {
      background: 'linear-gradient(92.25deg, #8B4FD0 -2.57%, #4A2470 113.38%)',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(155, 95, 218, 0.4)'
    },
    transition: 'all 0.3s ease-in-out'
  },
  submittingSpinner: {
    color: 'white',
    marginBottom: theme.spacing(3)
  }
}));

// Answer options with numeric values
const answerOptions = [
  { value: '0', label: 'Strongly disagree' },
  { value: '1', label: 'Disagree' },
  { value: '2', label: 'Neutral' },
  { value: '3', label: 'Agree' },
  { value: '4', label: 'Strongly agree' }
];

export default function HrmAssessment(): JSX.Element {
  const { isAuthenticated } = useAuth();
  const classes = useStyles();
  const { id: assessmentId } = useParams();
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [currentPage, setCurrentPage] = useState(0);
  const [focusedQuestionIndex, setFocusedQuestionIndex] = useState(0);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(0);
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const [showKeyboardGuide, setShowKeyboardGuide] = useState(true);
  const [testStarted, setTestStarted] = useState(false);
  const [testStartTime, setTestStartTime] = useState<string | null>(null);
  const [testEndTime, setTestEndTime] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const questionsPerPage = 4;
  const { t } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const progressStepperRef = useRef<HTMLDivElement | null>(null);

  const { BACK } = getButtons();
  const { questions, loading, error, submitting, submitSuccess } = useSelector(
    (state: RootState) => state.assessment
  );

  const { logo }: any = useSelector(
    (state: RootState) => state.generalRecruiter.company
  );

  // Secure approach: Only show questions when test has started
  const questionsToShow = testStarted ? questions : [];

  // Pagination calculations
  const totalPages = Math.ceil(questionsToShow.length / questionsPerPage);
  const startIndex = currentPage * questionsPerPage;
  const endIndex = startIndex + questionsPerPage;
  const currentQuestions = questionsToShow.slice(startIndex, endIndex);

  // Check if current page is complete
  const isCurrentPageComplete = currentQuestions.every(
    (question) =>
      answers[question.id] !== undefined && answers[question.id] !== ''
  );

  // Check if we can go to next page
  const canGoNext = currentPage < totalPages - 1 && isCurrentPageComplete;
  const canGoPrevious = currentPage > 0;

  // Load test state from localStorage
  const loadTestState = useCallback(() => {
    try {
      const savedState = localStorage.getItem('hrmTestState');
      if (savedState) {
        const testState = JSON.parse(savedState);

        // Check if this is for the same assessment
        if (testState.assessmentId === assessmentId) {
          // Check if session is not too old (max 2 hours)
          const maxAge = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
          if (Date.now() - testState.timestamp < maxAge) {
            return testState;
          }
          // Session expired, clear it
          localStorage.removeItem('hrmTestState');
        } else {
          // Different assessment, clear old state
          localStorage.removeItem('hrmTestState');
        }
      }
    } catch (error) {
      console.error('Error loading test state:', error);
      localStorage.removeItem('hrmTestState');
    }
    return null;
  }, [assessmentId]);

  // Clear test state
  const clearTestState = useCallback(() => {
    localStorage.removeItem('hrmTestState');
    setTestStarted(false);
    setTestStartTime(null);
    setTestEndTime(null);
    setSessionId(null);
  }, []);

  useEffect(() => {
    dispatch(fetchHrmQuestions());
  }, [dispatch]);

  // Restore test state on page load
  useEffect(() => {
    const savedState = loadTestState();
    if (savedState) {
      setTestStarted(savedState.testStarted);
      setTestStartTime(savedState.testStartTime);
      setTestEndTime(savedState.testEndTime || null);
      setSessionId(savedState.sessionId);
    }
  }, [assessmentId, loadTestState]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar('Failed to load assessment questions', {
        variant: 'error'
      });
    }
  }, [error, enqueueSnackbar]);

  useEffect(() => {
    if (submitSuccess) {
      enqueueSnackbar('Assessment submitted successfully', {
        variant: 'success'
      });
      // Clear test state only after successful submission
      // This prevents the start screen from appearing before redirect
      clearTestState();
      // Keep isSubmitting true until redirect happens
    }
  }, [submitSuccess, enqueueSnackbar, clearTestState]);

  // Reset isSubmitting if submission fails
  useEffect(() => {
    if (error && submitting === false && isSubmitting) {
      setIsSubmitting(false);
    }
  }, [error, submitting, isSubmitting]);

  const handleAnswerChange = (questionId: number, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value
    }));
  };

  // Pagination navigation functions
  const goToNextPage = useCallback(() => {
    if (canGoNext) {
      setCurrentPage((prev) => {
        const newPage = prev + 1;
        // Scroll to progress stepper after state update
        setTimeout(() => {
          if (progressStepperRef.current) {
            progressStepperRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest'
            });
          } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }, 100);
        return newPage;
      });
    }
  }, [canGoNext]);

  const goToPreviousPage = useCallback(() => {
    if (canGoPrevious) {
      setCurrentPage((prev) => {
        const newPage = prev - 1;
        // Scroll to progress stepper after state update
        setTimeout(() => {
          if (progressStepperRef.current) {
            progressStepperRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest'
            });
          } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }, 100);
        return newPage;
      });
    }
  }, [canGoPrevious]);

  // Keyboard navigation handlers
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const { key } = event;

      // Prevent default behavior for our custom keys
      if (
        [
          'ArrowUp',
          'ArrowDown',
          'ArrowLeft',
          'ArrowRight',
          'Enter',
          'Space',
          'Tab'
        ].includes(key)
      ) {
        event.preventDefault();
      }

      switch (key) {
        case 'ArrowDown':
        case 'ArrowRight': {
          // Move to next question or next option
          if (focusedQuestionIndex < currentQuestions.length - 1) {
            setFocusedQuestionIndex((prev) => prev + 1);
            setFocusedOptionIndex(0);
          } else if (focusedOptionIndex < answerOptions.length - 1) {
            setFocusedOptionIndex((prev) => prev + 1);
          }
          break;
        }

        case 'ArrowUp':
        case 'ArrowLeft': {
          // Move to previous question or previous option
          if (focusedOptionIndex > 0) {
            setFocusedOptionIndex((prev) => prev - 1);
          } else if (focusedQuestionIndex > 0) {
            setFocusedQuestionIndex((prev) => prev - 1);
            setFocusedOptionIndex(answerOptions.length - 1);
          }
          break;
        }

        case 'Enter':
        case ' ': {
          // Select current option
          const currentQuestion = currentQuestions[focusedQuestionIndex];
          const currentOption = answerOptions[focusedOptionIndex];
          if (currentQuestion && currentOption) {
            handleAnswerChange(currentQuestion.id, currentOption.value);
          }
          break;
        }

        case 'Tab': {
          // Navigate between questions or move to next page
          if (event.shiftKey) {
            // Shift+Tab: Go to previous question
            if (focusedQuestionIndex > 0) {
              setFocusedQuestionIndex((prev) => prev - 1);
              setFocusedOptionIndex(0);
            } else if (canGoPrevious) {
              goToPreviousPage();
              setFocusedQuestionIndex(currentQuestions.length - 1);
              setFocusedOptionIndex(0);
            }
          } else if (focusedQuestionIndex < currentQuestions.length - 1) {
            // Tab: Go to next question or next page
            setFocusedQuestionIndex((prev) => prev + 1);
            setFocusedOptionIndex(0);
          } else if (canGoNext) {
            goToNextPage();
            setFocusedQuestionIndex(0);
            setFocusedOptionIndex(0);
          }
          break;
        }

        case 'Escape': {
          // Toggle keyboard help or go back to previous page
          if (showKeyboardHelp) {
            setShowKeyboardHelp(false);
          } else if (canGoPrevious) {
            goToPreviousPage();
            setFocusedQuestionIndex(currentQuestions.length - 1);
            setFocusedOptionIndex(0);
          }
          break;
        }

        case 'F1': {
          // Show keyboard help
          event.preventDefault();
          setShowKeyboardHelp(true);
          break;
        }
      }
    },
    [
      focusedQuestionIndex,
      focusedOptionIndex,
      currentQuestions,
      canGoNext,
      canGoPrevious,
      goToNextPage,
      goToPreviousPage,
      showKeyboardHelp
    ]
  );

  // Add keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Reset focus when page changes
  useEffect(() => {
    setFocusedQuestionIndex(0);
    setFocusedOptionIndex(0);
  }, [currentPage]);

  // // Auto-navigate to next page when current page is complete
  // useEffect(() => {
  //   if (
  //     isCurrentPageComplete &&
  //     currentPage < totalPages - 1 &&
  //     !isManualNavigation
  //   ) {
  //     const timer = setTimeout(() => {
  //       goToNextPage();
  //       // Scroll to progress stepper after navigation
  //       setTimeout(() => {
  //         if (progressStepperRef.current) {
  //           progressStepperRef.current.scrollIntoView({
  //             behavior: 'smooth',
  //             block: 'start',
  //             inline: 'nearest'
  //           });
  //         } else {
  //           // Fallback to top of page if ref not available
  //           window.scrollTo({ top: 0, behavior: 'smooth' });
  //         }
  //       }, 200);
  //     }, 1000); // Auto-advance after 1 second

  //     return () => clearTimeout(timer);
  //   }
  //   return undefined;
  // }, [
  //   isCurrentPageComplete,
  //   currentPage,
  //   totalPages,
  //   goToNextPage,
  //   questionsPerPage,
  //   isManualNavigation
  // ]);

  // // Reset manual navigation flag when user changes any answer
  // useEffect(() => {
  //   if (isManualNavigation) {
  //     // Reset flag when user makes any answer change
  //     setIsManualNavigation(false);
  //   }
  // }, [answers, isManualNavigation]);

  // Generate unique session ID
  const generateSessionId = () =>
    `hrm_test_${Date.now()}_${Math.random().toString(36).substring(2)}`;

  // Save test state to localStorage
  const saveTestState = (
    startTime: string,
    sessionId: string,
    endTime?: string
  ) => {
    const testState = {
      testStarted: true,
      testStartTime: startTime,
      testEndTime: endTime || null,
      sessionId,
      assessmentId,
      timestamp: Date.now()
    };
    localStorage.setItem('hrmTestState', JSON.stringify(testState));
  };

  const handleStartTest = () => {
    const startTime = new Date().toISOString();
    const newSessionId = generateSessionId();

    setTestStartTime(startTime);
    setTestStarted(true);
    setSessionId(newSessionId);

    // Save to localStorage
    saveTestState(startTime, newSessionId);
  };

  const handleSubmit = async () => {
    if (!assessmentId) {
      enqueueSnackbar('Assessment ID is missing', { variant: 'error' });
      return;
    }

    if (!testStarted || !testStartTime || !sessionId) {
      enqueueSnackbar('Invalid test session. Please refresh and start again.', {
        variant: 'error'
      });
      return;
    }

    // Set local submitting state to ensure overlay stays visible until redirect
    setIsSubmitting(true);

    // Calculate end time in ISO 8601 format
    const testEndTime = new Date().toISOString();
    setTestEndTime(testEndTime);

    // Format answers as required by the API
    const formattedAnswers: HrmAnswerSubmission[] = Object.entries(answers).map(
      ([questionId, answer]) => ({
        QuestionID: parseInt(questionId, 10),
        Answer: parseInt(answer, 10)
      })
    );

    // Save updated state with end time
    saveTestState(testStartTime, sessionId, testEndTime);

    // Submit answers using the Redux action
    // Note: Don't clear test state here - it will be cleared after successful submission
    // to prevent showing the start screen before redirect
    try {
      await dispatch(
        submitHrmAnswers(
          assessmentId,
          formattedAnswers,
          testStartTime,
          testEndTime
        )
      );
      // If submission succeeds, redirect will happen in Redux action
      // isSubmitting will remain true until page redirects
    } catch (error) {
      // Only reset if there's an error (submission failed)
      setIsSubmitting(false);
    }
  };

  const isFormComplete =
    questionsToShow.length > 0 &&
    testStarted &&
    Object.keys(answers).length === questionsToShow.length;

  const isLastPage = currentPage === totalPages - 1;

  const title = t('HRM Assessment');

  return (
    <Page title={title}>
      <PageContainer>
        {isAuthenticated ? (
          <DashboardNavbar title={title} />
        ) : (
          <HomeNavbar logo={logo} login={false} />
        )}
        <Button
          onClick={() => navigate(-1)}
          variant="text"
          style={{
            color: '#9B5FDA',
            padding: 0
          }}
        >
          <ArrowBackIosNew
            style={{
              fontSize: '18px',
              margin: '0 0 5px 5px'
            }}
          />
          {BACK}
        </Button>
        <Container maxWidth="lg" style={{ paddingTop: '40px' }}>
          <Box sx={{ mb: 5 }}>
            <Typography variant="h4" gutterBottom>
              {t('Please answer all questions to complete the assessment')}
            </Typography>

            {/* Privacy Overlay */}
            {!testStarted && !submitting && !isSubmitting && (
              <Box className={classes.privacyOverlay}>
                <Box className={classes.privacyContent}>
                  <Typography className={classes.privacyTitle}>
                    {t('HRM Assessment')}
                  </Typography>
                  <Typography className={classes.privacyDescription}>
                    {t('Click "Start the Test" when you are ready to begin.')}
                  </Typography>
                  <Button
                    className={classes.startButton}
                    onClick={handleStartTest}
                    variant="contained"
                    size="large"
                    disabled={submitting}
                  >
                    {t('Start the Test')}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Test Already Started Warning */}
            {testStarted && testStartTime && (
              <Box
                sx={{
                  backgroundColor: '#e3f2fd',
                  border: '1px solid #2196f3',
                  borderRadius: 2,
                  p: 2,
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap'
                }}
              >
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ fontWeight: 600 }}
                >
                  {submitting || isSubmitting
                    ? t('Submitting Assessment...')
                    : t('Test in Progress')}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('Started at')}: {new Date(testStartTime).toLocaleString()}
                </Typography>
                {testEndTime && (
                  <Typography variant="body2" color="textSecondary">
                    {t('Ended at')}: {new Date(testEndTime).toLocaleString()}
                  </Typography>
                )}
              </Box>
            )}

            {/* Submitting Overlay - prevents interaction during submission */}
            {(submitting || isSubmitting) && (
              <Box className={classes.privacyOverlay}>
                <Box className={classes.privacyContent}>
                  <CircularProgress
                    size={60}
                    className={classes.submittingSpinner}
                  />
                  <Typography className={classes.privacyTitle}>
                    {t('Submitting Assessment...')}
                  </Typography>
                  <Typography className={classes.privacyDescription}>
                    {t(
                      'Please wait while we submit your answers. You will be redirected shortly.'
                    )}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Keyboard Navigation Guide */}
            {showKeyboardGuide && testStarted && (
              <Box
                sx={{
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  borderRadius: 2,
                  p: 2,
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                  position: 'relative'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span style={{ fontSize: '1.2em' }}>⌨️</span>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {t('Keyboard Navigation Available:')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <kbd
                      style={{
                        backgroundColor: '#e9ecef',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '0.8em',
                        fontFamily: 'monospace'
                      }}
                    >
                      ↑↓
                    </kbd>
                    <Typography variant="caption" color="textSecondary">
                      {t('Questions')}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <kbd
                      style={{
                        backgroundColor: '#e9ecef',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '0.8em',
                        fontFamily: 'monospace'
                      }}
                    >
                      ←→
                    </kbd>
                    <Typography variant="caption" color="textSecondary">
                      {t('Options')}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <kbd
                      style={{
                        backgroundColor: '#e9ecef',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '0.8em',
                        fontFamily: 'monospace'
                      }}
                    >
                      Enter
                    </kbd>
                    <Typography variant="caption" color="textSecondary">
                      {t('Select')}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <kbd
                      style={{
                        backgroundColor: '#e9ecef',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '0.8em',
                        fontFamily: 'monospace'
                      }}
                    >
                      Tab
                    </kbd>
                    <Typography variant="caption" color="textSecondary">
                      {t('Next')}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <kbd
                      style={{
                        backgroundColor: '#e9ecef',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '0.8em',
                        fontFamily: 'monospace'
                      }}
                    >
                      F1
                    </kbd>
                    <Typography variant="caption" color="textSecondary">
                      {t('Help')}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowKeyboardHelp(true)}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.8em',
                      color: '#9B5FDA',
                      '&:hover': {
                        backgroundColor: 'rgba(155, 95, 218, 0.1)'
                      }
                    }}
                  >
                    {t('View Full Guide')}
                  </Button>

                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowKeyboardGuide(false)}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.8em',
                      color: '#6c757d',
                      minWidth: 'auto',
                      px: 1,
                      '&:hover': {
                        backgroundColor: 'rgba(108, 117, 125, 0.1)'
                      }
                    }}
                    title={t('Hide keyboard guide')}
                  >
                    ✕
                  </Button>
                </Box>
              </Box>
            )}

            {/* Show keyboard guide button when hidden */}
            {!showKeyboardGuide && testStarted && (
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowKeyboardGuide(true)}
                  startIcon={<span>⌨️</span>}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.8em',
                    borderColor: '#e9ecef',
                    color: '#6c757d',
                    '&:hover': {
                      borderColor: '#9B5FDA',
                      color: '#9B5FDA',
                      backgroundColor: 'rgba(155, 95, 218, 0.05)'
                    }
                  }}
                >
                  {t('Show Keyboard Guide')}
                </Button>
              </Box>
            )}

            {/* Progress Stepper */}
            {questionsToShow.length > 0 && (
              <Box className={classes.progressStepper} ref={progressStepperRef}>
                {totalPages <= 8 ? (
                  <Stepper activeStep={currentPage} alternativeLabel>
                    {Array.from({ length: totalPages }, (_, index) => (
                      <Step key={index}>
                        <StepLabel>
                          {t('Page')} {index + 1}
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 1, sm: 0 }
                    }}
                  >
                    <Typography
                      variant="h6"
                      color="primary"
                      sx={{
                        mr: { xs: 0, sm: 2 },
                        mb: { xs: 1, sm: 0 }
                      }}
                    >
                      {t('Progress')}
                    </Typography>
                    <Box
                      sx={{
                        flexGrow: 1,
                        mx: { xs: 0, sm: 2 },
                        width: { xs: '100%', sm: 'auto' }
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          height: 8,
                          backgroundColor: 'grey.300',
                          borderRadius: 4,
                          overflow: 'hidden'
                        }}
                      >
                        <Box
                          sx={{
                            width: `${((currentPage + 1) / totalPages) * 100}%`,
                            height: '100%',
                            backgroundColor: 'primary.main',
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      {currentPage + 1}/{totalPages}
                    </Typography>
                  </Box>
                )}

                {/* Pagination Controls in Progress Bar */}
                {totalPages > 1 && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 2,
                      px: 2
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<NavigateBefore />}
                      onClick={goToPreviousPage}
                      disabled={!canGoPrevious}
                      size="small"
                      sx={{
                        minWidth: '100px',
                        borderRadius: '20px',
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      {t('Previous')}
                    </Button>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Typography variant="body2" color="textSecondary">
                        {t('Page')}{' '}
                        <span className={classes.currentPage}>
                          {currentPage + 1}
                        </span>{' '}
                        {t('of')} {totalPages}
                      </Typography>
                    </Box>

                    {isLastPage ? (
                      <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!isFormComplete || submitting || isSubmitting}
                        size="small"
                        sx={{
                          minWidth: '120px',
                          borderRadius: '20px',
                          textTransform: 'none',
                          fontWeight: 600,
                          background:
                            'linear-gradient(92.25deg, #9B5FDA -2.57%, #562C82 113.38%)',
                          '&:disabled': {
                            background: 'grey'
                          }
                        }}
                      >
                        {submitting ? (
                          <>
                            <CircularProgress
                              size={16}
                              color="inherit"
                              sx={{ mr: 1 }}
                            />
                            {t('Submitting...')}
                          </>
                        ) : (
                          t('Submit')
                        )}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        endIcon={<NavigateNext />}
                        onClick={goToNextPage}
                        disabled={!canGoNext}
                        className={classes.paginationButton}
                      >
                        {t('Next')}
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {loading ? (
            <Box className={classes.progressContainer}>
              <CircularProgress size={60} />
              <Typography variant="body1" sx={{ mt: 2 }}>
                {t('Loading assessment questions...')}
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Show only current page questions */}
              {currentQuestions.map((question, index) => (
                <Paper
                  key={question.id}
                  className={classes.questionCard}
                  sx={{
                    border:
                      focusedQuestionIndex === index
                        ? '2px solid #9B5FDA'
                        : '2px solid transparent',
                    boxShadow:
                      focusedQuestionIndex === index
                        ? '0 4px 12px rgba(155, 95, 218, 0.3)'
                        : 'none',
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <Typography
                    variant="h6"
                    className={classes.questionText}
                    sx={{
                      color:
                        focusedQuestionIndex === index ? '#9B5FDA' : 'inherit',
                      fontWeight: focusedQuestionIndex === index ? 700 : 600
                    }}
                  >
                    {startIndex + index + 1}. {question.question}
                    {focusedQuestionIndex === index && (
                      <span
                        style={{
                          marginLeft: '8px',
                          fontSize: '0.8em',
                          color: '#666'
                        }}
                      >
                        (Focused - Use arrow keys to navigate options)
                      </span>
                    )}
                  </Typography>
                  <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                      className={classes.radioGroup}
                      value={answers[question.id] || ''}
                      onChange={(e) =>
                        handleAnswerChange(question.id, e.target.value)
                      }
                    >
                      <Grid container spacing={2}>
                        {answerOptions.map((option, optionIndex) => (
                          <Grid item xs={12} sm={6} key={option.value}>
                            <FormControlLabel
                              value={option.value}
                              control={
                                <Radio
                                  color="primary"
                                  sx={{
                                    '&.Mui-checked': {
                                      color: '#9B5FDA'
                                    }
                                  }}
                                />
                              }
                              label={
                                <Box
                                  sx={{
                                    padding: '8px 12px',
                                    borderRadius: '8px',
                                    backgroundColor:
                                      focusedQuestionIndex === index &&
                                      focusedOptionIndex === optionIndex
                                        ? 'rgba(155, 95, 218, 0.1)'
                                        : 'transparent',
                                    border:
                                      focusedQuestionIndex === index &&
                                      focusedOptionIndex === optionIndex
                                        ? '2px solid #9B5FDA'
                                        : '2px solid transparent',
                                    transition: 'all 0.2s ease-in-out',
                                    fontWeight:
                                      focusedQuestionIndex === index &&
                                      focusedOptionIndex === optionIndex
                                        ? 600
                                        : 400
                                  }}
                                >
                                  {option.label} - {option.value}
                                  {focusedQuestionIndex === index &&
                                    focusedOptionIndex === optionIndex && (
                                      <span
                                        style={{
                                          marginLeft: '8px',
                                          fontSize: '0.8em',
                                          color: '#9B5FDA'
                                        }}
                                      >
                                        (Press Enter to select)
                                      </span>
                                    )}
                                </Box>
                              }
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </RadioGroup>
                  </FormControl>
                </Paper>
              ))}

              {/* Pagination Controls */}
              {questionsToShow.length > 0 && totalPages > 1 && (
                <Box className={classes.paginationContainer}>
                  <Button
                    variant="outlined"
                    startIcon={<NavigateBefore />}
                    onClick={goToPreviousPage}
                    disabled={!canGoPrevious}
                    className={classes.paginationButton}
                  >
                    {t('Previous')}
                  </Button>

                  <Box className={classes.pageIndicator}>
                    <Typography variant="body2" color="textSecondary">
                      {t('Page')}{' '}
                      <span className={classes.currentPage}>
                        {currentPage + 1}
                      </span>{' '}
                      {t('of')} {totalPages}
                    </Typography>
                  </Box>

                  {isLastPage ? (
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={!isFormComplete || submitting}
                      className={
                        !isFormComplete || submitting
                          ? classes.submitButtonDisabled
                          : classes.submitButton
                      }
                    >
                      {submitting ? (
                        <>
                          <CircularProgress
                            size={24}
                            color="inherit"
                            sx={{ mr: 1 }}
                          />
                          {t('Submitting...')}
                        </>
                      ) : (
                        t('Submit Assessment')
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      endIcon={<NavigateNext />}
                      onClick={goToNextPage}
                      disabled={!canGoNext}
                      className={classes.paginationButton}
                    >
                      {t('Next')}
                    </Button>
                  )}
                </Box>
              )}

              {/* Submit button for single page or when all questions are answered */}
              {questionsToShow.length > 0 && totalPages === 1 && (
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleSubmit}
                    disabled={!isFormComplete || submitting}
                    className={
                      !isFormComplete || submitting
                        ? classes.submitButtonDisabled
                        : classes.submitButton
                    }
                  >
                    {submitting ? (
                      <>
                        <CircularProgress
                          size={24}
                          color="inherit"
                          sx={{ mr: 1 }}
                        />
                        {t('Submitting...')}
                      </>
                    ) : (
                      t('Submit Assessment')
                    )}
                  </Button>
                </Box>
              )}

              {!isCurrentPageComplete && questionsToShow.length > 0 && (
                <Typography
                  variant="body2"
                  color="error"
                  align="center"
                  sx={{ mt: 2 }}
                >
                  {t('Please answer all questions on this page to continue')}
                </Typography>
              )}

              {questionsToShow.length === 0 && !loading && (
                <Box textAlign="center" py={5}>
                  <Typography variant="h6" color="textSecondary">
                    {t('No questions available for this assessment')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Container>

        {/* Keyboard Help Modal */}
        {showKeyboardHelp && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}
            onClick={() => setShowKeyboardHelp(false)}
          >
            <Paper
              sx={{
                p: 4,
                maxWidth: 600,
                maxHeight: '80vh',
                overflow: 'auto',
                borderRadius: 2,
                boxShadow: 24
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography variant="h5" gutterBottom color="primary">
                {t('Keyboard Navigation Help')}
              </Typography>

              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {t('Navigation')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 3 }}>
                  <li>
                    <strong>↑/↓ Arrow Keys:</strong>{' '}
                    {t('Move between questions')}
                  </li>
                  <li>
                    <strong>←/→ Arrow Keys:</strong>{' '}
                    {t('Move between answer options')}
                  </li>
                  <li>
                    <strong>Tab:</strong>{' '}
                    {t('Go to next question or next page')}
                  </li>
                  <li>
                    <strong>Shift + Tab:</strong>{' '}
                    {t('Go to previous question or previous page')}
                  </li>
                  <li>
                    <strong>Escape:</strong>{' '}
                    {t('Go back to previous page or close help')}
                  </li>
                </Box>

                <Typography variant="h6" gutterBottom>
                  {t('Answering')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 3 }}>
                  <li>
                    <strong>Enter:</strong>{' '}
                    {t('Select the focused answer option')}
                  </li>
                  <li>
                    <strong>Space:</strong>{' '}
                    {t('Select the focused answer option')}
                  </li>
                </Box>

                <Typography variant="h6" gutterBottom>
                  {t('Help')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 3 }}>
                  <li>
                    <strong>F1:</strong> {t('Show this help dialog')}
                  </li>
                </Box>

                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mt: 2 }}
                >
                  {t(
                    'You can complete the entire assessment using only the keyboard!'
                  )}
                </Typography>
              </Box>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={() => setShowKeyboardHelp(false)}
                  sx={{
                    background:
                      'linear-gradient(92.25deg, #9B5FDA -2.57%, #562C82 113.38%)',
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  {t('Got it!')}
                </Button>
              </Box>
            </Paper>
          </Box>
        )}

        {/* Keyboard Help Button */}
        {testStarted && (
          <Button
            variant="outlined"
            onClick={() => setShowKeyboardHelp(true)}
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              borderRadius: '50%',
              minWidth: '56px',
              height: '56px',
              zIndex: 1000,
              backgroundColor: 'white',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }}
            title={t('Keyboard Help (F1)')}
          >
            ⌨️
          </Button>
        )}
      </PageContainer>
    </Page>
  );
}
